/* eslint-disable @typescript-eslint/no-unsafe-call */

/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import api from "API";
import * as Params from "API/params";

import * as Emissions from "./payloads";

export const consolidatedEmissionsEndpoint = `/organisations/:org/consolidated-emissions/`;
export const consolidatedEmissionsExportEndpoint =
  consolidatedEmissionsEndpoint + `export/`;
export const consolidatedEmissionsAsyncExportEndpoint =
  consolidatedEmissionsEndpoint + `async-export/`;
export const consolidatedEmissionsCountEndpoint =
  consolidatedEmissionsEndpoint + `count/`;

const extendedEndpoints = api.injectEndpoints({
  endpoints: (builder) => ({
    consolidatedEmissions: builder.query<
      Emissions.GetConsolidatedEmissions,
      Params.GenericParamMap<Emissions.GetConsolidatedEmissionsParams>
    >({
      query: (query) => {
        const params: Emissions.ConsolidatedEmissionsEndpointParamsType = {
          page: query.page,
          date_from: Params.toString(query.date_from),
          date_to: Params.toString(query.date_to),
          group_by: Params.toString(query.group_by),
          ghg_scope: Params.toList(query.scope),
          ghg_category: Params.toList(query.scope3Category),
          tags: Params.toJSONArray(query.tags),
          location: Params.toArray(query.location),
          ordering: Params.toString(query.ordering),
          emission_output_type: Params.toString(query.emission_output_type),
          activity: Params.toArray(query.activity),
          activity_data: Params.toArray(query.activity_data),
          reported_without_activity_data: Params.toString(
            query.reported_without_activity_data
          ),
          entity: Params.toArray(query.entity),
          reporting_year: Params.toString(query.reporting_year),
        };

        return {
          url: consolidatedEmissionsEndpoint,
          params: Params.enrichParamsWithCustomAttributes({ params, query }),
        };
      },
    }),
    consolidatedEmissionDetails: builder.query<
      Emissions.GetConsolidatedEmissionDetails,
      Emissions.GetConsolidatedEmissionDetailsParams
    >({
      query: ({ emission_id, consolidation_id, reporting_year }) => ({
        url:
          consolidatedEmissionsEndpoint +
          `${emission_id}/` +
          `${consolidation_id}/`,
        params: {
          reporting_year,
        },
      }),
    }),
    consolidatedEmissionConsolidationDetails: builder.query<
      Emissions.GetConsolidatedEmissionConsolidationDetails,
      Emissions.GetConsolidatedEmissionDetailsParams
    >({
      query: ({ emission_id, consolidation_id, reporting_year }) => ({
        url:
          consolidatedEmissionsEndpoint +
          `${emission_id}/` +
          `${consolidation_id}/calculation-details`,
        params: {
          reporting_year,
        },
      }),
    }),
    consolidatedEmissionCalculationLog: builder.query<
      Emissions.GetConsolidatedEmissionCalculationLogItem[],
      Emissions.GetConsolidatedEmissionDetailsParams
    >({
      query: ({ emission_id, consolidation_id, reporting_year }) => ({
        url:
          consolidatedEmissionsEndpoint +
          `${emission_id}/` +
          `${consolidation_id}/calculation-logs`,
        params: {
          reporting_year,
        },
      }),
    }),
    consolidatedEmissionExport: builder.query<
      Blob,
      Params.GenericParamMap<Emissions.ExportConsolidatedEmissionsParams>
    >({
      query: (query) => {
        const params: Emissions.ConsolidatedEmissionsEndpointGenericParamsType =
          {
            date_from: Params.toString(query.date_from),
            date_to: Params.toString(query.date_to),
            ghg_scope: Params.toList(query.scope),
            ghg_category: Params.toList(query.scope3Category),
            location: Params.toArray(query.location),
            activity: Params.toArray(query.activity),
            entity: Params.toArray(query.entity),
            reporting_year: Params.toString(query.reporting_year),
          };

        return {
          url: consolidatedEmissionsExportEndpoint,
          params: Params.enrichParamsWithCustomAttributes({ params, query }),
          responseHandler: (response: Response) =>
            response.ok ? response.blob() : response.json(),
        };
      },
    }),
    consolidatedEmissionAsyncExport: builder.query<
      Blob,
      Params.GenericParamMap<Emissions.ExportConsolidatedEmissionsParams>
    >({
      query: (query) => {
        const params: Emissions.ConsolidatedEmissionsEndpointGenericParamsType =
          {
            date_from: Params.toString(query.date_from),
            date_to: Params.toString(query.date_to),
            ghg_scope: Params.toList(query.scope),
            ghg_category: Params.toList(query.scope3Category),
            location: Params.toArray(query.location),
            activity: Params.toArray(query.activity),
            entity: Params.toArray(query.entity),
            reporting_year: Params.toString(query.reporting_year),
          };

        return {
          url: consolidatedEmissionsAsyncExportEndpoint,
          params: Params.enrichParamsWithCustomAttributes({ params, query }),
          responseHandler: (response: Response) =>
            response.ok ? response.blob() : response.json(),
        };
      },
    }),
    consolidatedEmissionsGrid: builder.query<
      Emissions.GetConsolidatedEmissions,
      {
        startRow: number;
        endRow: number;
        sortModel?: { colId: string; sort: "desc" | "asc" }[];
        filterModel?: any;
        emissionOutputType?: string;
        dateFrom?: string;
        dateTo?: string;
        reporting_year?: string | null;
        is_estimated?: boolean;
      }
    >({
      query: ({
        startRow,
        endRow,
        sortModel,
        filterModel,
        emissionOutputType,
        dateFrom,
        dateTo,
        reporting_year,
        is_estimated,
      }) => {
        const limit = endRow - startRow;
        const page = Math.floor(startRow / limit) + 1;

        let ordering;
        if (sortModel && sortModel.length > 0) {
          const sort = sortModel[0];

          const colId =
            sort?.colId === "dateRange" ? "start_date,end_date" : sort?.colId;
          ordering = sort?.sort === "desc" ? `-${colId}` : colId;
        }

        const params = {
          page,
          page_size: limit,
          ordering,
          emission_output_type: emissionOutputType || "location",
          date_from: dateFrom,
          date_to: dateTo,
          reporting_year: Params.toString(reporting_year),
          ...(filterModel && { ...filterModel }),
          ...(is_estimated && { is_estimated }),
        };

        return {
          url: consolidatedEmissionsEndpoint,
          params,
        };
      },
    }),

    consolidatedEmissionCount: builder.query<
      number,
      Params.GenericParamMap<{ dateFrom?: string; dateTo?: string }>
    >({
      query: (query) => {
        return {
          url: consolidatedEmissionsCountEndpoint,
          params: {
            date_from: Params.toString(query.dateFrom),
            date_to: Params.toString(query.dateTo),
          },
        };
      },
    }),
  }),
});

export const {
  useConsolidatedEmissionsQuery,
  useConsolidatedEmissionDetailsQuery,
  useConsolidatedEmissionConsolidationDetailsQuery,
  useConsolidatedEmissionCalculationLogQuery,
  useConsolidatedEmissionCountQuery,
  useLazyConsolidatedEmissionExportQuery,
  useLazyConsolidatedEmissionAsyncExportQuery,
  useLazyConsolidatedEmissionsGridQuery,
} = extendedEndpoints;
